/**
 * F14 — Traslados de miembros entre clubes con apretón de manos, sin admin.
 *
 * El flujo (2026-08-10, caso Solange MUTIS → ÁGAPE):
 *   1. El director del club DESTINO intenta agregar a alguien que ya es
 *      miembro activo de otro club. En vez de crearle una segunda membresía
 *      (el hueco viejo), pide el traslado — con el teléfono del director de
 *      origen a la vista para coordinar de viva voz.
 *   2. Al director del club de ORIGEN le aparece la solicitud en su bandeja.
 *      Aprobar EJECUTA el traslado en la misma transacción (members.ts
 *      transferToClub): membresía nueva en destino, se elimina la de origen,
 *      historial, y la solicitud queda `completed`. No hay estado intermedio
 *      en que la persona esté en cero o en dos clubes.
 *   3. Rechazar o cancelar sólo cambia el estado de la solicitud.
 *
 * Permisos (ver firestore.rules):
 *   - La solicitud la crea el director del destino; la leen sólo los
 *     directores involucrados y el admin.
 *   - El member en el club destino lo crea el director de ORIGEN, cosa que
 *     normalmente está prohibida: las rules lo permiten únicamente si el
 *     mismo batch marca la solicitud como completada (get/getAfter sobre el
 *     MISMO id, porque solicitud y member comparten id determinístico
 *     `${targetClubId}__${personId}`).
 *
 * Los contadores de los clubes NO se tocan aquí: el trigger member-counters
 * los recuenta desde la verdad con cada escritura de members (el director de
 * origen no puede escribir el doc del club destino, y un contador a medias es
 * peor que un contador con un segundo de rezago).
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { memberService } from "./members";
import type { Member, MemberTransferRequest, Person } from "@/types";

const COLLECTION = "memberTransferRequests";

export const transferRequestId = (targetClubId: string, personId: string) =>
  `${targetClubId}__${personId}`;

/** "CC 63318912" → "CC •••8912": suficiente para reconocerla, sin regalar la ficha. */
export function maskDocument(type?: string, number?: string): string {
  const num = String(number ?? "");
  const tail = num.slice(-4);
  return `${type ?? "DOC"} •••${tail || "????"}`;
}

/**
 * Contacto del director general de un club (nombre + teléfono), para poner en
 * la solicitud. Best-effort: si el club sólo tiene directores de sección, se
 * toma el primero activo; si no hay teléfono en su ficha, va sin teléfono.
 */
export async function getDirectorContact(
  clubId: string,
): Promise<{ name?: string; phone?: string; userId?: string }> {
  const ras = await getDocs(
    query(
      collection(db, "roleAssignments"),
      where("scopeId", "==", clubId),
      where("status", "==", "active"),
    ),
  );
  const activos = ras.docs.map((d) => d.data());
  const director =
    activos.find((r) => r.role === "club_director" && !r.section) ??
    activos.find((r) => r.role === "club_director");
  if (!director) return {};

  let phone: string | undefined;
  try {
    const persons = await getDocs(
      query(
        collection(db, "persons"),
        where("linkedUserId", "==", director.userId),
      ),
    );
    phone = (persons.docs[0]?.data() as Person | undefined)?.phone;
  } catch {
    // sin teléfono: la solicitud sirve igual
  }
  return {
    name: (director.userName as string) ?? undefined,
    phone,
    userId: director.userId as string,
  };
}

export const memberTransferService = {
  /**
   * Crea (o re-crea, si una anterior fue rechazada/cancelada) la solicitud de
   * traslado. Lanza si ya hay una pendiente o si la persona ya se trasladó.
   */
  async request(input: {
    person: Pick<
      Person,
      "id" | "fullName" | "idDocumentType" | "idDocumentNumber"
    > &
      Partial<Pick<Person, "guardianUserId" | "linkedUserId">>;
    /** Membresía activa actual (en el club de origen). */
    membership: Pick<Member, "id" | "clubId"> & Partial<Pick<Member, "role">>;
    originClubName: string;
    targetClubId: string;
    targetClubName: string;
    requestedBy: string;
    requestedByName: string;
    requestedByPhone?: string;
    requestedByEmail?: string;
    note?: string;
  }): Promise<MemberTransferRequest> {
    const id = transferRequestId(input.targetClubId, input.person.id);
    const ref = doc(db, COLLECTION, id);

    // Si el doc NO existe, las rules niegan la lectura (la regla específica
    // evalúa resource.data sobre null): un doc ausente y uno ilegible se
    // tratan igual — como "no hay solicitud previa".
    const previa = await getDoc(ref).catch(() => null);
    if (previa?.exists() && previa.data().status === "pending") {
      throw new Error(
        "Ya hay una solicitud de traslado pendiente para esta persona. " +
          "Revísala en Solicitudes → Traslados.",
      );
    }
    if (previa?.exists() && previa.data().status === "completed") {
      throw new Error("Esta persona ya fue trasladada a tu club.");
    }

    const origen = await getDirectorContact(input.membership.clubId);

    // Sin undefined: Firestore los rechaza.
    const datos: Record<string, unknown> = {
      id,
      personId: input.person.id,
      personName: input.person.fullName,
      personDocMasked: maskDocument(
        input.person.idDocumentType,
        input.person.idDocumentNumber,
      ),
      originClubId: input.membership.clubId,
      originClubName: input.originClubName,
      originMemberId: input.membership.id,
      targetClubId: input.targetClubId,
      targetClubName: input.targetClubName,
      requestedBy: input.requestedBy,
      requestedByName: input.requestedByName,
      status: "pending",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (origen.name) datos.originDirectorName = origen.name;
    if (origen.phone) datos.originDirectorPhone = origen.phone;
    // El traslado no toca la ficha (el vínculo con el acudiente viaja igual),
    // pero el origen tiene que saber que decide sobre alguien a cargo.
    if (input.person.guardianUserId) datos.personHasGuardian = true;
    if (input.person.linkedUserId) datos.personHasOwnAccount = true;
    if (input.membership.role && input.membership.role !== "member")
      datos.originRole = input.membership.role;
    if (input.requestedByPhone) datos.requestedByPhone = input.requestedByPhone;
    if (input.requestedByEmail) datos.requestedByEmail = input.requestedByEmail;
    if (input.note?.trim()) datos.note = input.note.trim();

    await setDoc(ref, datos);
    return { ...(datos as unknown as MemberTransferRequest), id };
  },

  /**
   * Aprueba Y ejecuta: el traslado y el cierre de la solicitud van en la misma
   * transacción (ver members.transferToClub). La llama el director de ORIGEN.
   */
  async approve(
    req: MemberTransferRequest,
    userId: string,
    decisionNote?: string,
  ): Promise<void> {
    await memberService.transferToClub(
      req.originMemberId,
      req.targetClubId,
      userId,
      decisionNote || `Traslado aprobado a solicitud de ${req.targetClubName}`,
      { transferRequestId: req.id, decisionNote },
    );
  },

  /** La rechaza el director de origen. */
  async reject(
    requestId: string,
    userId: string,
    decisionNote?: string,
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTION, requestId), {
      status: "rejected",
      decidedBy: userId,
      decidedAt: serverTimestamp(),
      ...(decisionNote?.trim() ? { decisionNote: decisionNote.trim() } : {}),
      updatedAt: serverTimestamp(),
    });
  },

  /** La cancela quien la pidió (club destino), mientras siga pendiente. */
  async cancel(requestId: string, userId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, requestId), {
      status: "cancelled",
      decidedBy: userId,
      decidedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Solicitudes vivas donde este club es ORIGEN (le piden gente) o DESTINO
   * (pidió gente). Un solo listener por lado.
   */
  watchForClub(
    clubId: string,
    onData: (data: {
      entrantes: MemberTransferRequest[];
      salientes: MemberTransferRequest[];
    }) => void,
    onError?: (e: Error) => void,
  ): () => void {
    const estado = {
      entrantes: [] as MemberTransferRequest[],
      salientes: [] as MemberTransferRequest[],
    };
    const emit = () => onData({ ...estado });

    const unsubIn = onSnapshot(
      query(
        collection(db, COLLECTION),
        where("originClubId", "==", clubId),
        where("status", "==", "pending"),
      ),
      (snap) => {
        estado.entrantes = snap.docs.map(
          (d) => ({ ...d.data(), id: d.id }) as MemberTransferRequest,
        );
        emit();
      },
      onError,
    );
    // Las salientes se muestran también resueltas (para ver el rechazo/estado).
    const unsubOut = onSnapshot(
      query(collection(db, COLLECTION), where("targetClubId", "==", clubId)),
      (snap) => {
        estado.salientes = snap.docs
          .map((d) => ({ ...d.data(), id: d.id }) as MemberTransferRequest)
          .sort(
            (a, b) =>
              (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0),
          );
        emit();
      },
      onError,
    );
    return () => {
      unsubIn();
      unsubOut();
    };
  },
};
